import {
  createSlice,
  createEntityAdapter,
  PayloadAction,
  EntityState,
  EntityAdapter,
} from "@reduxjs/toolkit";
import { Intent } from "@/features/ryteTracker/types/goal";
import type { Goal } from "@/features/ryteTracker/types/goal";
import type { YardVisit } from "@/features/relayYardControl/types/visits";
import type { YardMove, GetYardMovesOutput } from "@/features/relayYardControl/types/moves";
import type { YardAppointment } from "@/features/ryteTracker/types/appointment";
import type { YardEventEnvelope } from "@/features/ryteTracker/types/yardEventEnvelope";
import { shouldUpdateBasedOnVersion } from "@/utils/versionUtils";
import { OPTIMISTIC_SEED_VERSION } from "@/features/ryteTracker/store/optimisticSeedVersion";
import rumService from "@/utils/awsRUM/awsRUM";
import { ryteApis } from "@/features/ryteTracker/services/ryteApis";
import { yardApis } from "@/features/relayYardControl/services/yardsApis";
import {
  extractVisitDemandIdentity,
  type DemandAliases,
} from "@/features/ryteTracker/utils/identity/resolveVisitFromDemand";
import { normalizeIncomingGoal } from "@/features/ryteTracker/store/normalizeIncomingGoal";
import { goalStartMs } from "@/features/ryteTracker/utils/puckRenderer/rebuildPuckFromGoals";
import {
  buildVisitEnrichment,
  type RetainedVisitEnrichment,
} from "@/features/ryteTracker/utils/pillEnrichment/buildVisitEnrichment";
import { resolveVisitEntityId } from "@/features/ryteTracker/utils/pillEnrichment/resolveVisitEntityId";

/**
 * Intents the RYTE Tracker UI never renders. Filtered out at every goal
 * ingest point (reducers + RTK Query extraReducers) so they never reach
 * the projector pipeline, the queue, the chart, or any consumer of
 * `selectAllRyteGoals`. Decision recorded in matrix §1 Intent table
 * + iteration log 2026-05-15.
 */
const NON_RENDERED_INTENTS: ReadonlySet<Intent> = new Set([
  Intent.ASSIGN_TRAILER,
  Intent.UNASSIGN_TRAILER,
]);

/** True when the goal's intent is renderable in RYTE. */
const isRenderableIntent = (goal: { intent: Intent }): boolean =>
  !NON_RENDERED_INTENTS.has(goal.intent);

/**
 * System identifier for GEM Arrivals pre-assignment goals. These goals are
 * created by the SesameGate service as part of the GEM Arrivals flow and are
 * not actionable or meaningful in the RYTE Tracker UI — they must be excluded
 * at the ingestion boundary so they never reach selectors, projector, queue,
 * chart, or detail panel.
 *
 * Exact string match only — no substring, no case-insensitive comparison.
 */
const GEM_ARRIVALS_SYSTEM = "SesameGateService" as const;

/**
 * True when the goal was NOT created by a system whose goals RYTE ignores.
 * Currently filters only GEM Arrivals pre-assignment goals
 * (createdBySystem === "SesameGateService"). Applied alongside
 * `isRenderableIntent` at the canonical ingestion boundary (`ingestGoal`).
 */
export const isIngestableBySystem = (goal: { createdBySystem?: string }): boolean =>
  goal.createdBySystem !== GEM_ARRIVALS_SYSTEM;

/**
 * Per-entry version gate shared by every upsertX / upsertManyX reducer +
 * the matchFulfilled extraReducers + replaceGoal.upsert. Returns true when
 * the incoming entity should overwrite (or insert), false when the slice
 * already has a same-id entity at a newer version.
 *
 * Centralizes the contract that was previously inlined 13 times — the next
 * change to gating semantics (e.g. RUM-warn on skipped upserts, custom
 * comparators per-entity) lives in one place.
 */
const shouldAcceptUpsert = <T extends { datapathViewVersion?: string }>(
  existing: T | undefined,
  incoming: T
): boolean => {
  if (!existing) return true;
  return shouldUpdateBasedOnVersion(existing.datapathViewVersion, incoming.datapathViewVersion);
};

/**
 * True when the version is the API-seed legacy default, which is EXACTLY `"0"`.
 *
 * Two narrowings, both load-bearing:
 *
 * 1. `=== "0"`, NOT "anything `parseVersionKey` rejects". Short unpadded strings
 *    like `"005"` are also unparseable, and treating those as legacy would let a
 *    stale `"003"` overwrite a newer `"005"`.
 *
 * 2. ABSENT is NOT legacy — `!version` is deliberately absent from this predicate.
 *    `"0"` means "came through a seed path, which had no version to record"; every
 *    seed path stamps it (`row.datapathViewVersion || "0"`) BEFORE the entity is
 *    stored, so a seeded entity always reads `"0"`, never absent. An entity whose
 *    version is absent therefore came from somewhere else — in practice the
 *    WebSocket path, where `ryteMessageProcessor` types
 *    `datapathViewVersion` as `string | undefined` (`:194`, `:229`, `:283`) and
 *    spreads it with no default. Treating absent as legacy made a version-less WS
 *    push indistinguishable from a seed, so both-legacy fired and a STALE REST
 *    refetch overwrote it: an operator watched a goal reach COMPLETED and then
 *    revert to CREATED with `isDelayed` cleared, on refetch, with no error.
 *    Absent now falls through to `shouldAcceptUpsert`, where
 *    `shouldUpdateBasedOnVersion(undefined, "0")` normalizes both sides to `"0"`
 *    and compares `"0" > "0"` → false → the seed loses and the WS record stands.
 *    Regression coverage: the `e-rows` in `apiSeedOverwrite.spec.ts`.
 *
 * D4 is unaffected by (2): its case is a seeded entity being re-seeded, and both
 * sides read `"0"` there. See the `a-rows` in the same spec.
 */
const isLegacySeedVersion = (version: string | undefined): boolean => version === "0";

/**
 * SEED-PATH-ONLY acceptance gate. Identical to `shouldAcceptUpsert` except that a
 * legacy-seed value may overwrite another legacy-seed value, so an API refetch is
 * no longer insert-only (D4). Both sides legacy means neither carries ordering
 * information, and the API response is the fresher read.
 *
 * `shouldUpdateBasedOnVersion("0", "0")` is `false` — `parseVersionKey` rejects
 * `"0"`, so both keys are null and the legacy branch compares `"0" > "0"`. That is
 * why a laptop waking from sleep refetches and lands nothing for any entity it
 * already holds, with no error and no failing test.
 *
 * NOT used by the WebSocket reducers — they keep `shouldAcceptUpsert`. A REAL
 * version (WS push, `applyGoalFromMutation`'s 19-digit stamp) and the FE
 * `OPTIMISTIC_SEED_VERSION` sentinel are both non-legacy, so a `"0"` seed cannot
 * clobber either. `src/utils/versionUtils.ts` is untouched (D14 — shared with
 * RelayYardControl).
 */
const shouldAcceptApiSeed = <T extends { datapathViewVersion?: string }>(
  existing: T | undefined,
  incoming: T
): boolean => {
  if (!existing) return true;
  if (
    isLegacySeedVersion(existing.datapathViewVersion) &&
    isLegacySeedVersion(incoming.datapathViewVersion)
  ) {
    return true;
  }
  return shouldAcceptUpsert(existing, incoming);
};

/** The five seed sources the catch-up window tracks independently. */
type CatchUpSeedSource = "goals" | "visits" | "appointments" | "events" | "moves";

/**
 * How long after arming a catch-up seed may still claim authority.
 *
 * Backstop, not a tuning knob. The window is consumed by the first response per
 * source, but a source whose query has NO live subscription never produces one —
 * `getYardMoves` is skipped without a `yardId`, appointments without a `site`. Left
 * unbounded, `pending` would stay armed and an unrelated fetch minutes later would
 * inherit authority it has no claim to.
 */
export const CATCH_UP_SEED_WINDOW_MS = 30_000;

/**
 * Acceptance gate for one seeded row. Receives the entity id so a gate may vary
 * its answer per entity — which the catch-up gate does and the ordinary one does not.
 *
 * Exists so the three seed policies (strict / D4-widened / catch-up) are chosen ONCE
 * per matcher and passed down, rather than each ingest helper carrying a widening
 * flag per policy. A boolean stopped being expressive at the second policy.
 */
type SeedGate = <T extends { datapathViewVersion?: string }>(
  existing: T | undefined,
  incoming: T,
  id: string
) => boolean;

/** The ordinary API-seed gate: D4-widened, id-independent. */
const apiSeedGate: SeedGate = (existing, incoming) => shouldAcceptApiSeed(existing, incoming);

/**
 * CATCH-UP-ONLY gate, armed by `armRyteCatchUp` after a WebSocket reconnect.
 *
 * The problem it solves: seeds stamp `datapathViewVersion: "0"`, which `parseVersionKey`
 * refuses, so `shouldUpdateBasedOnVersion` rules it older than every real version. That
 * default is right for a routine refetch racing live pushes — and exactly backwards after
 * an outage, where the socket delivered nothing and the REST read is the only fresh copy.
 * Under `shouldAcceptApiSeed` a catch-up lands entities CREATED during the gap (no existing
 * entity to lose against) and silently drops every MODIFICATION to an entity the socket had
 * already written — a goal that reached COMPLETED mid-outage keeps rendering as in-progress,
 * with no error and no RUM signal (the gate-rejection warn in `ingestGoal` filters `"0"`
 * losses as benign).
 *
 * So: while the window is open, a seed row overwrites the pre-outage record unconditionally.
 *
 * `wsWritten` is what keeps that safe. The socket resumes streaming ~500ms BEFORE the
 * catch-up fires (`REFETCH_DELAY` in `createConnectionHandlers`), and the five refetches
 * resolve independently after that — so live writes and seed writes genuinely overlap. Any
 * id written by a non-seed path since arming falls back to the strict-for-real-versions
 * `shouldAcceptApiSeed`, so a fresh push, an optimistic stamp, or a mutation response is
 * never rolled back to whatever the REST read happened to hold.
 *
 * Known residual hazard, accepted: a REST request issued BEFORE the socket died and still
 * unresolved after reconnect will consume the window and apply as authoritative, reverting
 * anything the socket wrote between that request's server read and the disconnect. Narrow
 * (needs a request pending across the whole outage) and not new — it already applies to
 * `"0"`-versioned entities under `shouldAcceptApiSeed`; this widens it to WS-versioned ones.
 * Bounded by `CATCH_UP_SEED_WINDOW_MS` and by one-shot consumption per source. Only the API
 * returning a real `datapathViewVersion` removes it — then ordinary comparison applies and
 * none of this is needed.
 *
 * That "accepted" covers exactly one shape: a request predating the arm that recorded the
 * live writes. It does NOT extend to a request predating a LATER arm — a flapping socket
 * re-arming a few hundred ms on — because that one IS reducible from this side, and is
 * reduced: `armRyteCatchUp` carries `wsWritten` across a re-arm, so an id a live path wrote
 * during the earlier window is still guarded when the earlier window's response finally
 * consumes the later one. The window still cannot tell the two requests apart; it no longer
 * needs to, because it no longer forgets. f25–f28 in `catchUpSeed.spec.ts` pin it.
 *
 * Does NOT address deletions. The seed expresses "gone" as an absent row and nothing reads
 * absence, so an entity DELETED during the outage stays in the slice until `clearRyteData`.
 * That needs a completeness guarantee the response does not carry, not a gate change.
 */
const catchUpSeedGate =
  (wsWritten: Record<string, true>, source: CatchUpSeedSource): SeedGate =>
  (existing, incoming, id) => {
    if (!existing) return true;
    // A live write landed after arming — the stream is authoritative again.
    if (wsWritten[`${source}:${id}`]) return shouldAcceptApiSeed(existing, incoming);
    // Pre-outage record. The REST read is the fresher of the two.
    return true;
  };

/**
 * Generic API-seed loop. Only getYardAppointments routes through here today —
 * events moved to `seedEventEntities` (below) for bulk-write + intra-payload
 * duplicate handling. Both were structurally identical
 * filter/map/gate loops: drop rows whose id is missing (RUM-warn with the
 * entity-specific message), default the API-seed `datapathViewVersion` to
 * `"0"`, then per-entry value-based version gate (`shouldAcceptApiSeed`)
 * before `upsertOne`.
 *
 * Semantics preserved exactly per entity type:
 *  - The missing-id RUM warn message stays entity-specific via `idLabel`, so
 *    telemetry keeps the entity label (do NOT collapse to one generic string).
 *  - `"0"` is the documented API-seed default (data-layer.md) — the WS path
 *    never applies it, so it lives here, not in the upsert reducers.
 *  - The gate is value-based (BigInt magnitude via `shouldUpdateBasedOnVersion`)
 *    applied per entry against the current slice entity — supplied by the caller
 *    as a `SeedGate`, normally `apiSeedGate`, so a `"0"` seed may overwrite a `"0"`
 *    entity and a refetch is not insert-only (D4). A real version still wins.
 *
 * The goals path intentionally does NOT route through here — it has its own
 * normalize + renderable-intent filter + gate-rejection telemetry (`ingestGoal`).
 * Visits use `ingestVisit` directly for retained-enrichment integration.
 */
const seedEntities = <T extends { datapathViewVersion?: string }>(
  entityState: EntityState<T, string>,
  adapter: EntityAdapter<T, string>,
  rows: readonly T[],
  getId: (row: T) => string | undefined,
  idLabel: string,
  gate: SeedGate,
  onAccepted?: (value: T) => void
): void => {
  for (const row of rows) {
    const id = getId(row);
    if (!id) {
      rumService.warn(`${idLabel} missing ID — skipping normalization during API seed`, { row });
      continue;
    }
    const value = { ...row, datapathViewVersion: row.datapathViewVersion || "0" };
    if (gate(entityState.entities[id], value, id)) {
      adapter.upsertOne(entityState, value);
      onAccepted?.(value);
    }
  }
};

/**
 * True when a version string represents a real engine/WS version rather
 * than the API-seed default. The seed normalizes every goal to `"0"`, and
 * a missing version reads as empty — both fire on every refetch, so they
 * are excluded from the gate-rejection telemetry below to keep it
 * signal-only.
 *
 * `OPTIMISTIC_SEED_VERSION` is excluded for the same reason: two optimistic
 * dispatches for one goal are a benign no-op, and counting the sentinel as
 * "real" would start emitting a rejection warn on that path. This is why the
 * seed gate keys off `isLegacySeedVersion` instead of reusing this predicate —
 * the two need different answers for the sentinel.
 */
const isRealVersion = (version: string | undefined): boolean =>
  !!version && version !== "0" && version !== OPTIMISTIC_SEED_VERSION;

/**
 * Canonical goal-ingest path. The version gate is shared across all entity
 * types via `shouldAcceptUpsert`, but the gate-rejection RUM warn lives
 * HERE — scoped to the goals adapter only — because the XD-unlink drop
 * (and that bug class generally) is goal-specific, and warning on every
 * visit/appointment/event rejection would be pure noise. Routing every
 * goal upsert call site (`upsertGoal`, `upsertManyGoals`,
 * `getYardGoals.matchFulfilled`, `replaceGoal`) through this one helper
 * means the warn covers them all without a per-call-site flag.
 *
 * The warn fires ONLY when BOTH versions are real (non-"0", non-empty) —
 * i.e. a genuine version conflict where an incoming update lost. The
 * benign seed-vs-seed "0">"0" no-op that fires on every refetch is
 * filtered out so the telemetry stays signal-only.
 */
const ingestGoal = (
  goalsState: EntityState<VersionedGoal, string>,
  raw: VersionedGoal,
  /**
   * Supplied ONLY by the `getYardGoals` API seed, which needs a widened gate so a
   * refetch is not insert-only. The WS reducers pass nothing and keep the strict
   * gate.
   */
  seedGate?: SeedGate
): void => {
  const incoming = normalizeIncomingGoal(raw);
  if (!isRenderableIntent(incoming)) return;
  if (!isIngestableBySystem(incoming)) return;
  const existing = goalsState.entities[incoming.goalId.id];
  const accept = seedGate
    ? seedGate(existing, incoming, incoming.goalId.id)
    : shouldAcceptUpsert(existing, incoming);
  if (!accept) {
    if (
      isRealVersion(existing?.datapathViewVersion) &&
      isRealVersion(incoming.datapathViewVersion)
    ) {
      rumService.warn("Goal upsert rejected by version gate", {
        goalId: incoming.goalId.id,
        existingVersion: existing?.datapathViewVersion,
        incomingVersion: incoming.datapathViewVersion,
      });
    }
    return;
  }
  goalsAdapter.upsertOne(goalsState, incoming);
};

// ─── Extended types with version field ───────────────────────────────────────

/** Goal with optional datapathViewVersion for WebSocket conflict resolution. */
export type VersionedGoal = Goal & { datapathViewVersion?: string };

/** YardVisit already has optional datapathViewVersion from the RYC type. */
export type VersionedVisit = YardVisit;

/** Appointment with optional version and gamma-only mock ownership marker. */
export type VersionedAppointment = YardAppointment & {
  datapathViewVersion?: string;
  isMockData?: true;
};

/** Event envelope with optional version for WebSocket conflict resolution. */
export type VersionedYardEventEnvelope = YardEventEnvelope & {
  datapathViewVersion?: string;
};

/**
 * A yard move (from `getYardMoves` REST + the WebSocket DAS envelope).
 * `YardMove` already carries an optional `datapathViewVersion`, so — like
 * `VersionedVisit` — no extension is needed; the alias exists only so the
 * moves adapter reads consistently with the other four entity types.
 */
export type VersionedMove = YardMove;

/**
 * Retained pill-level enrichment snapshot keyed by Visit ID. Survives
 * `removeVisit` / `removeManyVisits` so that pucks whose live Visit was
 * removed (DETACH_HAUL_ACTION) can still render carrier/equipment/badge
 * data on the pill. Cleared on `clearRyteData` (site change).
 *
 * Each entry stores the `datapathViewVersion` at which the snapshot was
 * captured — the version gate rejects a stale Visit re-arrival that is
 * older than the snapshot (Yard-5946 requirement #3).
 */
export interface RetainedVisitEnrichmentEntry {
  datapathViewVersion: string;
  enrichment: RetainedVisitEnrichment;
}

// ─── Entity Adapters ─────────────────────────────────────────────────────────

export const goalsAdapter = createEntityAdapter<VersionedGoal, string>({
  selectId: (goal) => goal.goalId.id,
  // Use the REMOVE-aware shim from rebuildPuckFromGoals — REMOVE goals
  // post-Part-1 omit `executionWindow.start` and would otherwise sort
  // to epoch via `?? 0`, breaking selectAllRyteGoals consumers and
  // primaryGoal selection in goalMerger. The shim falls back to
  // `delayedAfter` for REMOVE so sort stays stable across both
  // legacy (start-present) and new (start-absent) wire shapes.
  sortComparer: (a, b) => goalStartMs(a) - goalStartMs(b),
});

export const visitsAdapter = createEntityAdapter<VersionedVisit, string>({
  selectId: (visit) => {
    return resolveVisitEntityId(visit);
  },
});

export const appointmentsAdapter = createEntityAdapter<VersionedAppointment, string>({
  selectId: (appointment) => {
    return appointment.appointmentId || "";
  },
});

export const eventsAdapter = createEntityAdapter<VersionedYardEventEnvelope, string>({
  selectId: (event) => event.eventId || "",
  sortComparer: (a, b) => {
    const aTs = a.data?.eventNotification?.metadata?.operationInvokedTimestamp ?? 0;
    const bTs = b.data?.eventNotification?.metadata?.operationInvokedTimestamp ?? 0;
    return aTs - bTs;
  },
});

export const movesAdapter = createEntityAdapter<VersionedMove, string>({
  selectId: (move) => move.moveId,
});

/**
 * Seeds one getYardEvents response with a single sorted-adapter mutation.
 *
 * The prior path called `upsertOne` for every row. Because the event adapter
 * is sorted, that repeatedly maintained the growing ID array and made initial
 * hydration superlinear. Collect accepted rows first, then let `upsertMany`
 * sort and merge the complete batch once.
 *
 * `acceptedById` is also the comparison baseline for later duplicate IDs in
 * the same response. This preserves the old sequential version-gate result:
 * a stale later duplicate cannot overwrite a newer row accepted earlier in
 * the payload.
 */
const seedEventEntities = (
  entityState: EntityState<VersionedYardEventEnvelope, string>,
  rows: readonly VersionedYardEventEnvelope[],
  gate: SeedGate
): void => {
  const acceptedById = new Map<string, VersionedYardEventEnvelope>();

  for (const row of rows) {
    const id = row.eventId;
    if (!id) {
      rumService.warn("Event missing ID — skipping normalization during API seed", { row });
      continue;
    }

    const value = { ...row, datapathViewVersion: row.datapathViewVersion || "0" };
    // Split deliberately: the intra-payload duplicate contract must stay strict
    // (first row wins on a "0"/"0" tie), while the comparison against the SLICE is
    // widened so a refetch is not insert-only.
    const fromPayload = acceptedById.get(id);
    const accept = fromPayload
      ? shouldAcceptUpsert(fromPayload, value) // intra-payload: unchanged
      : gate(entityState.entities[id], value, id); // vs slice: caller's policy
    if (accept) {
      const existing = fromPayload ?? entityState.entities[id];
      acceptedById.set(id, existing ? { ...existing, ...value } : value);
    }
  }

  if (acceptedById.size > 0) {
    eventsAdapter.upsertMany(entityState, Array.from(acceptedById.values()));
  }
};

// ─── State Interface ─────────────────────────────────────────────────────────

export interface RyteDataState {
  goals: EntityState<VersionedGoal, string>;
  visits: EntityState<VersionedVisit, string>;
  appointments: EntityState<VersionedAppointment, string>;
  events: EntityState<VersionedYardEventEnvelope, string>;
  /**
   * Yard moves, keyed by `moveId`. Seeded from `getYardMoves` (REST) and
   * updated by the RYTE WebSocket move envelope through the batcher, exactly
   * like the other four entities. Deliberately NOT part of the first-paint
   * readiness gate (`seededSources`) — moves never move, add, or re-anchor a
   * chart bar, so the chart's first stable paint does not wait on them.
   */
  moves: EntityState<VersionedMove, string>;
  /**
   * Lightweight VRID→visitId and ISA→visitId alias maps. Populated on
   * every accepted Visit upsert and API seed. Retained across
   * `removeVisit` / `removeManyVisits` so that VRID-only / ISA-only
   * goals can still resolve their operational visitId after the live
   * Visit entity is removed from the slice. Cleared on `clearRyteData`
   * (site change). Never stores full Visit entities — only the one-way
   * string alias (demand-key → visitId).
   *
   * Null-prototype maps defend against prototype pollution from
   * externally-sourced keys (VRID/ISA from API/WebSocket).
   *
   * Typed via the shared `DemandAliases` interface rather than re-declared
   * inline: consumers resolve against that type, and a second structural copy
   * here is how the two silently diverge when a map is added.
   */
  demandAliases: DemandAliases;
  /**
   * Retained pill-level Visit enrichment snapshots, keyed by Visit ID.
   * Populated on every accepted Visit upsert (WS + API seed). Survives
   * `removeVisit` / `removeManyVisits` so pucks whose live Visit was
   * removed (DETACH_HAUL_ACTION) retain carrier/equipment/badge data.
   * Cleared on `clearRyteData` (site change). No pruning in v1 — the
   * map is site-session bounded.
   *
   * Null-prototype map defends against prototype pollution from
   * externally-sourced Visit IDs (API/WebSocket).
   */
  retainedVisitEnrichment: Record<string, RetainedVisitEnrichmentEntry>;
  /**
   * Monotonic site-generation counter. Incremented every time the user
   * switches sites (via `clearRyteData`). Consumers that dispatch
   * optimistically after an async boundary (`createGoal` response,
   * `updateGoal` response, or any other post-await dispatch) can capture
   * the generation at dispatch time and compare it when the promise
   * resolves — if it advanced, the dispatch belongs to a different site
   * and should be skipped.
   *
   * This is the belt-and-suspenders guard to the `extraReducers`
   * version-check. RTK Query responses for the old yard can still land
   * after `clearRyteData` — this counter lets consumers drop them.
   */
  siteGeneration: number;
  /**
   * Monotonic generation for the goal REST read. Unlike `siteGeneration`, a
   * WebSocket reconnect advances this without resetting chart-local state.
   * The fan-out query compares its captured value before starting each UTC-day
   * request and before fulfilling, so refresh/reconnect work cannot land late.
   */
  goalReadEpoch: number;
  /**
   * The site the slice currently holds data for, as BOTH keys the RTK Query
   * seed requests are scoped by. `null` means "not yet set" and disables the
   * gate, so existing callers and tests behave exactly as before.
   *
   * Why both: the four seed requests do not agree on a scope key.
   * `GetYardGoalsRequest.yardId` is required, `GetYardEventsRequest.yardId` is
   * OPTIONAL, `GetYardVisitsInput.yardId` is required but lives on `yardApis`
   * (shared with RelayYardControl) rather than `ryteApis`, and
   * `GetYardAppointmentsRequest` carries `buildingCode` instead. Storing one key
   * would leave a quarter of the seed surface ungated while the gate reported
   * success — and the visits seed, being on the other API slice, is the one that
   * was in fact missed on the first pass. All four matchers below now call
   * `isStaleSiteSeed`; keep it that way when adding a fifth.
   *
   * This is the API-side THIRD of site-switch containment — it covers an in-flight
   * REST response for the PREVIOUS yard resolving after `clearRyteData()`. There are
   * three parts, not two, and all three are needed:
   *   1. this gate — in-flight REST seeds for the old site
   *   2. `batcher.discard()` — entities already BUFFERED from the old site
   *   3. `isForeignSiteFrame` in `ryteMessageProcessor` — frames that ARRIVE after
   *      the switch, which (2) cannot see because they are not buffered yet
   * An earlier version of this comment credited `discard()` with "the WebSocket
   * half"; it only ever covered (2), leaving (3) open.
   */
  currentYardId: string | null;
  currentBuildingCode: string | null;
  /**
   * Which of the four seed sources have written to this slice for the site it
   * currently holds. Consumed by `useRyteData`'s first-paint readiness gate.
   *
   * **Why a reducer-written latch and not the RTK Query hook flags.** Three
   * things a query flag cannot tell you, each of which would open the gate
   * against an empty slice:
   *   1. `isSuccess` / `data` are STICKY across an arg change — for one render
   *      after `yardId` changes the hooks report success carrying the PREVIOUS
   *      site's payload, which is exactly the render where `clearRyteData` has
   *      already emptied the slice.
   *   2. `fulfilled` does not imply seeded. A cache hit makes RTK bail inside
   *      the thunk's `condition` without dispatching `fulfilled` at all, so no
   *      matcher runs; and when one does run, `isStaleSiteSeed` can still
   *      reject the write.
   *   3. Payload length cannot stand in for it either. Zero events is a
   *      documented steady state, cross-dock LOAD generates no events, and the
   *      goal seed legitimately filters an entire response down to nothing
   *      (non-renderable intents, GEM Arrivals). "Seeded with zero rows" and
   *      "never seeded" are indistinguishable by count.
   *
   * Each flag is set as the FIRST statement after its matcher's
   * `isStaleSiteSeed` early return and BEFORE any entity processing — so a
   * rejected seed never latches, and a fully-filtered response still does.
   * Keep that placement identical across all four when adding a fifth.
   *
   * Reset by `clearRyteData`, i.e. on BOTH a site switch and a manual refresh —
   * and in both cases the gate genuinely RE-CLOSES. The latch in `useRyteData`
   * is keyed on `siteGeneration` (which `clearRyteData` bumps), not on `site`,
   * so a refresh re-gates on purpose: otherwise the operator sees a flash of the
   * outgoing bars.
   *
   * Re-closing is only safe because `RyteChartColumn` COVERS the chart with an
   * opaque overlay rather than withholding items from a visible chart. A closed
   * gate performs no DataSet removals, so the stale bars are still in the
   * DataSet — merely hidden — and the next sync's diff removes them. Withhold
   * items from a *visible* chart and those same stale bars freeze on screen,
   * which is worse than having no gate at all.
   */
  seededSources: {
    goals: boolean;
    visits: boolean;
    appointments: boolean;
    events: boolean;
  };
  /**
   * Authoritative catch-up window, armed by `armRyteCatchUp` when the RYTE
   * WebSocket reconnects. See `catchUpSeedGate` for what it buys and what it costs.
   *
   * Disarmed (`armedAt: null`) is the normal state and makes every seed behave
   * exactly as it did before this existed — the whole mechanism is inert unless a
   * reconnect armed it, which is why `clearRyteData` resets it rather than
   * preserving it.
   */
  catchUpSeed: {
    /**
     * Sources still owed one authoritative seed. Consumed by the FIRST response per
     * source — per-source rather than one shared flag because the five refetches
     * resolve independently and in no fixed order, so one flag would hand the whole
     * window to whichever query happened to answer first.
     */
    pending: Record<CatchUpSeedSource, boolean>;
    /**
     * `${source}:${id}` for every entity a non-seed path wrote since arming. Prefixed
     * because ids are only unique per adapter (`goalId` / `visitId` / `moveId` share no
     * namespace) and a bare key could collide across sources.
     *
     * Null-prototype: keys interpolate externally-sourced ids.
     */
    wsWritten: Record<string, true>;
    /**
     * Arm time in epoch ms, or null when disarmed. Supplied by the caller rather than
     * read from `Date.now()` here so the reducer stays pure and the window is testable.
     */
    armedAt: number | null;
  };
}

/** Fresh (all-false) seed-latch state. Shared by `initialState` and the reset. */
const emptySeededSources = (): RyteDataState["seededSources"] => ({
  goals: false,
  visits: false,
  appointments: false,
  events: false,
});

/** Disarmed catch-up state. Shared by `initialState` and the reset. */
const emptyCatchUpSeed = (): RyteDataState["catchUpSeed"] => ({
  pending: { goals: false, visits: false, appointments: false, events: false, moves: false },
  wsWritten: Object.create(null) as Record<string, true>,
  armedAt: null,
});

const initialState: RyteDataState = {
  goals: goalsAdapter.getInitialState(),
  visits: visitsAdapter.getInitialState(),
  appointments: appointmentsAdapter.getInitialState(),
  events: eventsAdapter.getInitialState(),
  moves: movesAdapter.getInitialState(),
  demandAliases: {
    vridToVisitId: Object.create(null) as Record<string, string>,
    isaToVisitId: Object.create(null) as Record<string, string>,
    locationPlanToVrid: Object.create(null) as Record<string, { vrid: string; visitId: string }>,
  },
  retainedVisitEnrichment: Object.create(null) as Record<string, RetainedVisitEnrichmentEntry>,
  siteGeneration: 0,
  goalReadEpoch: 0,
  currentYardId: null,
  currentBuildingCode: null,
  seededSources: emptySeededSources(),
  catchUpSeed: emptyCatchUpSeed(),
};

const isMockEntityId = (id: string): boolean => id.startsWith("mock-");

const removeAliasesForMockVisits = (aliases: Record<string, string>): void => {
  for (const [alias, visitId] of Object.entries(aliases)) {
    if (isMockEntityId(visitId)) delete aliases[alias];
  }
};

/**
 * Same teardown for the plan→VRID map, whose VALUE is a `{ vrid, visitId }`
 * pair rather than a bare visitId. The originating `visitId` is retained on the
 * entry precisely so mock pairings can be evicted here — a flat
 * `planId → vrid` map would leave synthetic VRIDs behind after
 * `clearRyteMockData`, labelling real pucks with mock identities.
 */
const removePlanPairsForMockVisits = (
  pairs: Record<string, { vrid: string; visitId: string }>
): void => {
  for (const [planId, pair] of Object.entries(pairs)) {
    if (isMockEntityId(pair.visitId)) delete pairs[planId];
  }
};

/**
 * Extracts VRID and ISA demand aliases from a Visit entity and updates the
 * slice's `demandAliases` maps. Called on every accepted visit upsert (both
 * WS-path and API-seed). The maps are intentionally NOT cleared on
 * `removeVisit` / `removeManyVisits` — that's the entire point of the
 * alias mechanism: a VRID-only puck retains its operational visitId after
 * the live Visit entity is removed.
 *
 * First-write semantics per key: if a VRID/ISA already maps to a visitId,
 * we overwrite unconditionally because the incoming visit is version-gated
 * upstream (only a newer version reaches here), so its identity links are
 * authoritative.
 */
const updateDemandAliases = (state: RyteDataState, visit: VersionedVisit): void => {
  // Reads the visit's identity directly. This used to build a whole
  // single-entry `VisitDemandLookup` and pick four key sets back off it, which
  // made the plan→VRID pairing reachable ONLY through a lookup — so every
  // whole-yard `buildVisitDemandLookup` consumer paid to compute a map none of
  // them could read.
  const { visitId, vrids, isaIds, planPairs } = extractVisitDemandIdentity(visit);
  if (!visitId) return;

  for (const vrid of vrids) {
    state.demandAliases.vridToVisitId[vrid] = visitId;
  }
  for (const isaId of isaIds) {
    state.demandAliases.isaToVisitId[isaId] = visitId;
  }

  // locationPlanId → VRID, the only source that can label a plan-only goal with
  // no visit linkage and no appointment. Same overwrite semantics as the two
  // maps above (the incoming visit is version-gated upstream, so it wins), which
  // is also what bounds staleness: a re-plan arriving on any visit corrects the
  // pairing. A CONFLICT — same plan id, a different VRID — is worth observing,
  // since the whole design assumes one plan maps to at most one VRID.
  for (const planId of Object.keys(planPairs)) {
    const incoming = planPairs[planId];
    const existing = state.demandAliases.locationPlanToVrid[planId];

    // Unchanged pairing — skip the write entirely. `incoming` is FRESHLY
    // ALLOCATED by `extractVisitDemandIdentity`, so it is never `===` the stored
    // entry and an unconditional assignment could never be the Immer no-op that
    // the two primitive-valued writes above get for free — it would mark the
    // draft modified on EVERY accepted visit upsert carrying a pairing.
    // `visitDemandLookupKeysEqual` compares `demandAliases` by REFERENCE
    // precisely because "visit-only updates that leave aliases untouched retain
    // the reference" (`enrichedPuckSelectors.ts`), so churning it here would push
    // a new `selectVisitDemandLookup` output to the enriched-puck factory, the
    // projector selectors and the pill enrichment map on every visit WS tick.
    if (existing && existing.vrid === incoming.vrid && existing.visitId === incoming.visitId) {
      continue;
    }

    // Below the guard on purpose: the warn describes a pairing we are ABOUT TO
    // change, so it must not fire for a write that does not happen. A persistent
    // conflict re-warning every steady-state tick would burn the 20-line
    // production warn cap for no new information.
    if (existing && existing.vrid !== incoming.vrid) {
      // Identifiers only, no operator/PII — a warn payload can reach a
      // production console. See `analytics-tracking.md` "Console fallbacks".
      rumService.warn("[ryte] visit haul identifiers map locationPlanId to multiple VRIDs", {
        locationPlanId: planId,
        previousVrid: existing.vrid,
        previousVisitId: existing.visitId,
        incomingVrid: incoming.vrid,
        incomingVisitId: incoming.visitId,
      });
    }

    state.demandAliases.locationPlanToVrid[planId] = incoming;
  }
};

/**
 * Canonical Visit ingest helper — version-gates against BOTH the live
 * entity adapter AND the retained enrichment snapshot, then writes to
 * both surfaces. Used by `upsertVisit`, `upsertManyVisits`, and the
 * `getYardVisits.matchFulfilled` API seed (Yard-5946 requirement #3).
 *
 * Version gate semantics:
 *   - If the live entity exists with a newer version → reject (no-op).
 *   - If the live entity is absent (removed) but the retained snapshot
 *     has a newer version → reject (stale re-arrival must not overwrite
 *     the snapshot or re-enter the live adapter).
 *   - Otherwise accept: upsert live entity, update demand aliases,
 *     and refresh the retained enrichment snapshot.
 */
const ingestVisit = (
  state: RyteDataState,
  incoming: VersionedVisit,
  /** Supplied only by the `getYardVisits` API seed. See `ingestGoal`. */
  seedGate?: SeedGate
): void => {
  const id = resolveVisitEntityId(incoming);
  if (!id) return;

  // Gate against live entity first. Widened on the seed path only.
  const existing = state.visits.entities[id];
  const acceptLive = seedGate
    ? seedGate(existing, incoming, id)
    : shouldAcceptUpsert(existing, incoming);
  if (existing && !acceptLive) return;

  // Gate against retained snapshot (covers post-removal stale re-arrival).
  // DELIBERATELY still strict on EVERY seed path — the reconnect catch-up included,
  // which is why `seedGate` is not consulted here: this gate exists so a stale
  // re-arrival cannot resurrect a removed Visit into the live adapter (Yard-5946
  // req #3). A "0"-stamped seed carries no evidence it is newer than the removal,
  // so widening it would let an additive, possibly-stale getYardVisits response
  // re-materialise a trailer that a DETACH_HAUL_ACTION just removed — the more
  // dangerous direction, and one an operator cannot explain. Revisit only when
  // removals carry versions.
  const retained = state.retainedVisitEnrichment[id];
  if (
    !existing &&
    retained &&
    !shouldUpdateBasedOnVersion(retained.datapathViewVersion, incoming.datapathViewVersion)
  ) {
    return;
  }

  // Accept: upsert live entity.
  visitsAdapter.upsertOne(state.visits, incoming);
  updateDemandAliases(state, incoming);

  // Refresh retained enrichment snapshot. The ENRICHMENT always takes the incoming
  // payload (it is the fresher read), but the recorded VERSION is a high-water mark:
  // it never regresses.
  //
  // Load-bearing because that version has exactly one reader — the resurrect gate 12
  // lines above — where a regression is a hole, not a cosmetic inaccuracy. An armed
  // reconnect catch-up accepts a `"0"`-stamped seed over a WS-versioned live visit, and
  // stamping `"0"` here would drop the snapshot from a real version to `"0"`. A later
  // DETACH plus a STALE WS re-arrival at an OLDER real version would then compare
  // `shouldUpdateBasedOnVersion("0", older-real)` → true and resurrect a trailer that
  // is gone — the exact Yard-5946 req #3 direction the gate exists to block, reopened
  // from behind.
  const priorRetained = state.retainedVisitEnrichment[id];
  const incomingVersion = incoming.datapathViewVersion || "0";
  state.retainedVisitEnrichment[id] = {
    datapathViewVersion:
      priorRetained &&
      !shouldUpdateBasedOnVersion(priorRetained.datapathViewVersion, incomingVersion)
        ? priorRetained.datapathViewVersion
        : incomingVersion,
    enrichment: buildVisitEnrichment(incoming),
  };
};

/**
 * Canonical Appointment ingest helper — version-gates the incoming entity and
 * normalizes the `isMockData` marker so that a real update to a previously-mock
 * appointment explicitly clears the marker. Without this normalization, RTK
 * Toolkit's shallow-merge upsert (`Object.assign`) preserves the old `true`
 * when the incoming real payload simply omits the field — causing
 * `clearRyteMockData` to incorrectly remove a now-real appointment.
 *
 * Contract: `isMockData` is exactly `true` only when the incoming entity
 * explicitly carries it; otherwise it is set to `undefined` so the adapter
 * overwrites any prior marker. The version gate is unchanged — a stale real
 * update must NOT reclassify the existing entity.
 */
const ingestAppointment = (
  appointmentsState: EntityState<VersionedAppointment, string>,
  incoming: VersionedAppointment
): void => {
  const id = incoming.appointmentId || "";
  const existing = appointmentsState.entities[id];
  if (!shouldAcceptUpsert(existing, incoming)) return;
  // Normalize: explicitly set isMockData so the adapter's shallow merge
  // overwrites any prior marker rather than preserving it from the old entity.
  const normalized: VersionedAppointment = {
    ...incoming,
    isMockData: incoming.isMockData === true ? true : undefined,
  };
  appointmentsAdapter.upsertOne(appointmentsState, normalized);
};

// ─── Slice ───────────────────────────────────────────────────────────────────

/**
 * True when a seed response is for a DIFFERENT site than the slice currently
 * holds, and must therefore be dropped.
 *
 * Inert when the stored scope is `null` (initial load, and immediately after
 * `clearRyteData`) so the first seed for a site is never rejected. Also inert
 * when the request carried no scope key — `GetYardEventsRequest.yardId` is
 * optional, and rejecting an unscoped request would drop legitimate data.
 */
const isStaleSiteSeed = (stored: string | null, requested: string | null | undefined): boolean =>
  stored !== null && requested != null && requested !== stored;

/**
 * Records that a NON-SEED path wrote `id`, so an overlapping catch-up seed cannot roll
 * it back. No-op unless a reconnect armed the window, which is the normal case — so
 * this costs one null check on the hot WebSocket batch path.
 *
 * Called from every live write reducer, not just the WebSocket ones. An optimistic
 * stamp (`OPTIMISTIC_SEED_VERSION`) and a mutation response both land through
 * `upsertGoal` / `replaceGoal`, and both are newer than any pre-outage REST read —
 * "the socket wrote it" is the common case, "something other than a seed wrote it" is
 * the actual invariant.
 *
 * Deliberately records the ATTEMPT, not the acceptance. A push rejected by the strict
 * gate means the slice already holds something newer still, so the seed must lose
 * either way; gating on acceptance would need the ingest helpers to report back for no
 * behavioural gain.
 *
 * Removals are NOT recorded: if a live path removed the entity, the API will not return
 * it either, so there is nothing for a seed to overwrite.
 *
 * The `armedAt === null` guard is also what BOUNDS this map. `wsWritten` grows one key per
 * live-written entity, and a busy yard writes thousands per shift, so recording must stop
 * as soon as no seed can still consult it — `disarmSpentCatchUp` is the other half of that
 * and the reason a disarm is not merely tidiness.
 */
const noteLiveWrite = (
  state: RyteDataState,
  source: CatchUpSeedSource,
  id: string | undefined
): void => {
  if (state.catchUpSeed.armedAt === null || !id) return;
  state.catchUpSeed.wsWritten[`${source}:${id}`] = true;
};

/**
 * Disarms once the window can no longer affect any seed, which is what keeps `wsWritten`
 * from growing for the rest of the session. Two spent conditions:
 *
 *  - every source consumed its one-shot — the normal path, reached within a second or two
 *    of the reconnect;
 *  - a response arrived past `CATCH_UP_SEED_WINDOW_MS`. `armedAt` is shared by all five
 *    sources, so one late response proves the window is dead for ALL of them, not just its
 *    own. This is the path that matters for a source whose query has no live subscription
 *    and therefore never consumes: without it `armedAt` would stay set indefinitely and
 *    every subsequent WS write would keep appending keys.
 *
 * Reducers have no clock, so expiry can only ever be observed when a timestamped response
 * arrives. Seeds arrive regularly (polling, invalidations), so this self-heals rather than
 * needing a timer.
 */
const disarmSpentCatchUp = (state: RyteDataState, lateResponse: boolean): void => {
  if (state.catchUpSeed.armedAt === null) return;
  const anyPending = Object.values(state.catchUpSeed.pending).some(Boolean);
  if (lateResponse || !anyPending) {
    state.catchUpSeed = emptyCatchUpSeed();
  }
};

/**
 * Picks the gate for `source`'s seed and CONSUMES the catch-up window if it is open.
 * Called exactly once per seed matcher, after the stale-site guard and the
 * `seededSources` latch — so a response dropped for the wrong site neither latches nor
 * burns the window.
 *
 * Consumes even when the payload is empty: a zero-row response is still THE catch-up
 * read for that source, and a later unrelated fetch must not inherit authority from it.
 *
 * `fulfilledTimeStamp` is the only clock RTK Query exposes on a fulfilled action —
 * `startedTimeStamp` is on the PENDING action, which no matcher here sees. So the window
 * cannot be scoped to "requests issued after the disconnect", and no amount of work HERE
 * changes that; it is why `catchUpSeedGate`'s residual hazard is answered by what the
 * ledger REMEMBERS across a re-arm rather than by identifying the stale request. A response
 * that fulfilled BEFORE arming is rejected outright rather than clamped: it cannot be a
 * catch-up read.
 */
const resolveSeedGate = (
  state: RyteDataState,
  source: CatchUpSeedSource,
  fulfilledAt: number | undefined
): SeedGate => {
  // `wsWritten` is captured BEFORE any disarm below. A disarm replaces `state.catchUpSeed`
  // wholesale, and the gate returned from this call must still see the ids recorded for THIS
  // payload — the closure holding the old reference is what makes that work.
  const { pending, wsWritten, armedAt } = state.catchUpSeed;
  if (armedAt === null) return apiSeedGate;

  // Expiry is only observable when a timestamped response arrives; an absent timestamp
  // proves nothing either way.
  if (fulfilledAt !== undefined && fulfilledAt - armedAt > CATCH_UP_SEED_WINDOW_MS) {
    disarmSpentCatchUp(state, true);
    return apiSeedGate;
  }

  // Not this response's window: already consumed for this source, no timestamp, or it
  // fulfilled before the arm.
  if (!pending[source] || fulfilledAt === undefined || fulfilledAt < armedAt) {
    disarmSpentCatchUp(state, false);
    return apiSeedGate;
  }

  pending[source] = false;
  disarmSpentCatchUp(state, false);
  return catchUpSeedGate(wsWritten, source);
};

const ryteDataSlice = createSlice({
  name: "ryteData",
  initialState,
  reducers: {
    // ── Goals ──────────────────────────────────────────────────────────
    upsertGoal: (state, action: PayloadAction<VersionedGoal>) => {
      // Routes through the canonical 5-step ingest sequence: normalize
      // wire-shape nulls → drop non-renderable intents → drop GEM Arrivals
      // (`isIngestableBySystem`) → version-gate → upsert. See `ingestGoal`
      // for the full contract.
      ingestGoal(state.goals, action.payload);
      noteLiveWrite(state, "goals", action.payload.goalId?.id);
    },
    upsertManyGoals: (state, action: PayloadAction<VersionedGoal[]>) => {
      // Per-entry version gate (mirrors `upsertGoal` above). The prior
      // `upsertMany(state.goals, ...)` form skipped the gate entirely,
      // letting stale-version batches downgrade newer slice state. Spec:
      // `.claude/specs/ryte-websocket-batching/` Req 3 + T1.
      for (const raw of action.payload) {
        ingestGoal(state.goals, raw);
        noteLiveWrite(state, "goals", raw.goalId?.id);
      }
    },
    removeGoal: (state, action: PayloadAction<string>) => {
      goalsAdapter.removeOne(state.goals, action.payload);
    },
    removeManyGoals: (state, action: PayloadAction<string[]>) => {
      goalsAdapter.removeMany(state.goals, action.payload);
    },
    /**
     * Atomic remove + upsert in a single reducer call. Used by the
     * optimistic-temp-REMOVE-seed flow to swap a synthetic temp REMOVE
     * for a real one without ever exposing a tick where the slice
     * contains both. `removeId` runs unconditionally; `upsert` runs the
     * same per-entry version gate as `upsertGoal` so a stale-version
     * payload (e.g. FE's createGoal "0" after WS already delivered
     * "0001:...") cannot downgrade slice state.
     *
     * Spec: `.claude/specs/optimistic-temp-remove-seed/` design.md
     * §"replaceGoal slice reducer (F2 fix)".
     */
    replaceGoal: (state, action: PayloadAction<{ removeId: string; upsert: VersionedGoal }>) => {
      const { removeId, upsert } = action.payload;
      // Contract: removeId MUST be a `~temp:` (or legacy `temp:`) goalId.
      // The reducer is dedicated to swapping FE-synthesized optimistic
      // goals for their real engine counterparts; calling it with a real
      // engine id would silently delete a real goal from the slice. Guard
      // surfaces the misuse loudly in dev (RUM warn) instead of producing
      // a 1+-render-cycle UI corruption that's hard to trace. Skip the
      // destructive removeOne; let upsert proceed via the gate below.
      // Spec: `optimistic-temp-remove-seed/` DA round 5 F-R5-2. The warn
      // can read `upsert.goalId.id` directly — `normalizeIncomingGoal`
      // only rewrites `executionWindow`, never touches `goalId`.
      if (!removeId.startsWith("~temp:") && !removeId.startsWith("temp:")) {
        rumService.warn(
          `replaceGoal: removeId "${removeId}" is not a temp goalId — refusing to delete`,
          { removeId, upsertId: upsert.goalId.id }
        );
      } else {
        goalsAdapter.removeOne(state.goals, removeId);
      }
      // Same canonical 5-step ingest as upsertGoal — boundary normalize +
      // intent filter + GEM-Arrivals filter + version gate + upsert. Atomic with the removeOne
      // above: both happen inside the same Immer producer, so consumers
      // never observe the intermediate "neither id present" state.
      ingestGoal(state.goals, upsert);
      noteLiveWrite(state, "goals", upsert.goalId?.id);
    },

    // ── Visits ─────────────────────────────────────────────────────────
    upsertVisit: (state, action: PayloadAction<VersionedVisit>) => {
      ingestVisit(state, action.payload);
      noteLiveWrite(state, "visits", resolveVisitEntityId(action.payload));
    },
    upsertManyVisits: (state, action: PayloadAction<VersionedVisit[]>) => {
      for (const incoming of action.payload) {
        ingestVisit(state, incoming);
        noteLiveWrite(state, "visits", resolveVisitEntityId(incoming));
      }
    },
    removeVisit: (state, action: PayloadAction<string>) => {
      visitsAdapter.removeOne(state.visits, action.payload);
    },
    removeManyVisits: (state, action: PayloadAction<string[]>) => {
      visitsAdapter.removeMany(state.visits, action.payload);
    },

    // ── Appointments ───────────────────────────────────────────────────
    upsertAppointment: (state, action: PayloadAction<VersionedAppointment>) => {
      ingestAppointment(state.appointments, action.payload);
      noteLiveWrite(state, "appointments", action.payload.appointmentId);
    },
    upsertManyAppointments: (state, action: PayloadAction<VersionedAppointment[]>) => {
      for (const incoming of action.payload) {
        ingestAppointment(state.appointments, incoming);
        noteLiveWrite(state, "appointments", incoming.appointmentId);
      }
    },
    removeAppointment: (state, action: PayloadAction<string>) => {
      appointmentsAdapter.removeOne(state.appointments, action.payload);
    },
    removeManyAppointments: (state, action: PayloadAction<string[]>) => {
      appointmentsAdapter.removeMany(state.appointments, action.payload);
    },

    // ── Events ─────────────────────────────────────────────────────────
    upsertEvent: (state, action: PayloadAction<VersionedYardEventEnvelope>) => {
      const incoming = action.payload;
      noteLiveWrite(state, "events", incoming.eventId);
      const existing = state.events.entities[incoming.eventId || ""];
      if (!shouldAcceptUpsert(existing, incoming)) return;
      eventsAdapter.upsertOne(state.events, incoming);
    },
    upsertManyEvents: (state, action: PayloadAction<VersionedYardEventEnvelope[]>) => {
      for (const incoming of action.payload) {
        noteLiveWrite(state, "events", incoming.eventId);
        const existing = state.events.entities[incoming.eventId || ""];
        if (!shouldAcceptUpsert(existing, incoming)) continue;
        eventsAdapter.upsertOne(state.events, incoming);
      }
    },
    removeEvent: (state, action: PayloadAction<string>) => {
      eventsAdapter.removeOne(state.events, action.payload);
    },
    removeManyEvents: (state, action: PayloadAction<string[]>) => {
      eventsAdapter.removeMany(state.events, action.payload);
    },

    // ── Moves ──────────────────────────────────────────────────────────
    // Version-gated with the shared `shouldAcceptUpsert` (WS path) — a DELETED
    // move envelope carries no version, so a same-moveId re-arrival can't
    // downgrade a newer record. Mirrors the Events reducers exactly.
    upsertMove: (state, action: PayloadAction<VersionedMove>) => {
      const incoming = action.payload;
      if (!incoming.moveId) return;
      noteLiveWrite(state, "moves", incoming.moveId);
      const existing = state.moves.entities[incoming.moveId];
      if (!shouldAcceptUpsert(existing, incoming)) return;
      movesAdapter.upsertOne(state.moves, incoming);
    },
    upsertManyMoves: (state, action: PayloadAction<VersionedMove[]>) => {
      for (const incoming of action.payload) {
        if (!incoming.moveId) continue;
        noteLiveWrite(state, "moves", incoming.moveId);
        const existing = state.moves.entities[incoming.moveId];
        if (!shouldAcceptUpsert(existing, incoming)) continue;
        movesAdapter.upsertOne(state.moves, incoming);
      }
    },
    removeMove: (state, action: PayloadAction<string>) => {
      movesAdapter.removeOne(state.moves, action.payload);
    },
    removeManyMoves: (state, action: PayloadAction<string[]>) => {
      movesAdapter.removeMany(state.moves, action.payload);
    },

    // ── Clear mock data ────────────────────────────────────────────────
    clearRyteMockData: (state) => {
      goalsAdapter.removeMany(state.goals, state.goals.ids.filter(isMockEntityId));
      visitsAdapter.removeMany(state.visits, state.visits.ids.filter(isMockEntityId));
      appointmentsAdapter.removeMany(
        state.appointments,
        state.appointments.ids.filter(
          (id) => isMockEntityId(id) || state.appointments.entities[id]?.isMockData === true
        )
      );
      eventsAdapter.removeMany(state.events, state.events.ids.filter(isMockEntityId));
      movesAdapter.removeMany(state.moves, state.moves.ids.filter(isMockEntityId));

      removeAliasesForMockVisits(state.demandAliases.vridToVisitId);
      removeAliasesForMockVisits(state.demandAliases.isaToVisitId);
      removePlanPairsForMockVisits(state.demandAliases.locationPlanToVrid);
      for (const visitId of Object.keys(state.retainedVisitEnrichment)) {
        if (isMockEntityId(visitId)) delete state.retainedVisitEnrichment[visitId];
      }
    },

    // ── Clear all ──────────────────────────────────────────────────────
    clearRyteData: (state) => {
      goalsAdapter.removeAll(state.goals);
      visitsAdapter.removeAll(state.visits);
      appointmentsAdapter.removeAll(state.appointments);
      eventsAdapter.removeAll(state.events);
      movesAdapter.removeAll(state.moves);
      // Reset demand aliases — a site change invalidates all prior mappings,
      // including the plan→VRID pairings (a location plan is site-scoped).
      state.demandAliases = {
        vridToVisitId: Object.create(null) as Record<string, string>,
        isaToVisitId: Object.create(null) as Record<string, string>,
        locationPlanToVrid: Object.create(null) as Record<
          string,
          { vrid: string; visitId: string }
        >,
      };
      // Clear retained enrichment — a site change invalidates all snapshots.
      state.retainedVisitEnrichment = Object.create(null) as Record<
        string,
        RetainedVisitEnrichmentEntry
      > as typeof state.retainedVisitEnrichment;
      // Bump site generation — consumers with in-flight async work captured
      // a prior value and will drop their post-await dispatches when they
      // see this incremented.
      state.siteGeneration += 1;
      state.goalReadEpoch += 1;
      // Drop the scope with the data. `useRyteData` re-establishes it for the
      // new site; until then the gate is inert, which matches the pre-gate
      // behaviour rather than silently rejecting the first seed.
      state.currentYardId = null;
      state.currentBuildingCode = null;
      // Re-arm the first-paint seed latches. See `seededSources` for why this
      // firing on a manual refresh (not just a site switch) is safe.
      state.seededSources = emptySeededSources();
      // Disarm any catch-up window. Both callers empty the slice, so every seed
      // that follows inserts against nothing and needs no authority — and leaving
      // it armed would let a stale `wsWritten` set from the OLD site suppress rows
      // of the new one.
      state.catchUpSeed = emptyCatchUpSeed();
    },

    /**
     * Arms the authoritative catch-up window after a WebSocket reconnect. Dispatched
     * by `RyteView`'s `onReconnect` handler immediately BEFORE `invalidateTags`, since
     * an invalidate can resolve from cache within the same tick.
     *
     * Deliberately does NOT clear the slice — that is `clearRyteData`, i.e. the manual
     * refresh, which blanks the operator's board and resets zoom. A reconnect is not
     * something the operator did, so nothing visible may change except data getting
     * more correct.
     *
     * Re-arming while ALREADY armed CARRIES `wsWritten` OVER rather than resetting it, and
     * that is load-bearing. A flapping socket arms twice a few hundred ms apart; RTK Query's
     * queryThunk `condition` refuses a duplicate request while one is pending, so arm #2's
     * `invalidateTags` starts NOTHING and the response that eventually consumes arm #2's
     * one-shot is the STALE one arm #1 requested. Reset the ledger and that response consults
     * an empty map, so it overwrites an entity a live push updated during arm #1 — a goal that
     * reached COMPLETED reverting to CREATED, silently, with no error and no RUM signal.
     * Retaining a key can only send the gate BACK to the strict `shouldAcceptApiSeed`, never
     * loosen it, so carrying over is the conservative direction.
     *
     * See `catchUpSeedGate` for the gate this switches on, and its residual hazard.
     */
    armRyteCatchUp: (state, action: PayloadAction<{ armedAt: number }>) => {
      state.goalReadEpoch += 1;
      // A disarmed window starts a fresh ledger: `clearRyteData` disarms on a site switch,
      // and the old site's ids must not suppress rows of the new one. Every disarm path
      // already leaves the map empty (`noteLiveWrite` records only while armed), so this
      // is where that invariant is ENFORCED rather than assumed — a `disarmSpentCatchUp`
      // that someday only nulls `armedAt` must not silently leak a whole session of keys
      // into the next window.
      if (state.catchUpSeed.armedAt === null) {
        state.catchUpSeed.wsWritten = Object.create(null) as Record<string, true>;
      }
      state.catchUpSeed.pending = {
        goals: true,
        visits: true,
        appointments: true,
        events: true,
        moves: true,
      };
      state.catchUpSeed.armedAt = action.payload.armedAt;
    },

    /**
     * Records which site the slice holds data for. Dispatched by `useRyteData`
     * once the yardId/buildingCode pair for the active site resolves.
     *
     * Must run AFTER `clearRyteData` on a switch, which is the natural order:
     * `useSiteManager`'s effect clears, then the new site's ids resolve.
     */
    setRyteSiteScope: (
      state,
      action: PayloadAction<{ yardId: string | null; buildingCode: string | null }>
    ) => {
      state.currentYardId = action.payload.yardId;
      state.currentBuildingCode = action.payload.buildingCode;
    },
  },
  extraReducers: (builder) => {
    builder
      // Seed goals from getYardGoals API response
      // API returns goals in Lambda envelope format: { data: Goal, goalId, yardId }
      // Unwrap goal.data to get the actual Goal record for the entity adapter
      .addMatcher(ryteApis.endpoints.getYardGoals.matchFulfilled, (state, action) => {
        const { payload } = action;
        if (isStaleSiteSeed(state.currentYardId, action.meta?.arg?.originalArgs?.yardId)) {
          rumService.warn("Dropped goal seed for a previous site", {
            requested: action.meta?.arg?.originalArgs?.yardId,
            current: state.currentYardId,
          });
          return;
        }
        // First-paint seed latch — set before entity processing, so a response
        // whose goals are all filtered out below still counts as seeded. See
        // `seededSources`.
        state.seededSources.goals = true;
        // Resolved once for the whole payload — and consumes the catch-up window if
        // one is open. See `resolveSeedGate`.
        const gate = resolveSeedGate(state, "goals", action.meta?.fulfilledTimeStamp);
        const goals: VersionedGoal[] = (payload.goals || [])
          .map((envelope) => envelope.data?.goal)
          .filter((goal): goal is Goal => {
            if (!goal?.goalId?.id) {
              rumService.warn("Goal missing goalId.id — skipping normalization during API seed", {
                goal,
              });
              return false;
            }
            return true;
          })
          // ASSIGN_TRAILER / UNASSIGN_TRAILER are never rendered in RYTE —
          // dropped at the API ingest boundary alongside the WebSocket
          // path's filter in `upsertGoal` / `upsertManyGoals` above.
          .filter(isRenderableIntent)
          // GEM Arrivals pre-assignment goals (createdBySystem ===
          // "SesameGateService") are excluded — they are not actionable in
          // RYTE. Same predicate runs inside `ingestGoal` for the WS path.
          .filter(isIngestableBySystem)
          .map((goal) => {
            // Boundary normalization: map engine null start/delayedAfter
            // to omitted fields. Same pattern as the WS path's upsertGoal
            // reducer — every slice-ingest site goes through this helper.
            const normalized = normalizeIncomingGoal(goal);
            return {
              ...normalized,
              datapathViewVersion: (normalized as VersionedGoal).datapathViewVersion || "0",
            };
          });
        // The `.map()` above applies the API-seed-specific
        // `datapathViewVersion: ... || "0"` default + missing-goalId warn
        // — those steps stay outside the helper because the WS path
        // doesn't need them. `ingestGoal` re-runs the idempotent
        // normalization + intent filter so the helper has one contract
        // regardless of caller.
        for (const goal of goals) {
          // Seed gate: a "0" seed may refresh a "0" entity (D4), and may overwrite a
          // real version outright while a reconnect catch-up window is open.
          ingestGoal(state.goals, goal, gate);
        }
      })
      // Seed visits from getYardVisits API response — uses the canonical
      // ingestVisit helper so retained enrichment + version gate against
      // retained snapshots is applied uniformly (Yard-5946 requirement #4).
      .addMatcher(
        yardApis.endpoints.getYardVisits.matchFulfilled,
        (
          state,
          action: {
            payload: unknown;
            meta?: { arg?: { originalArgs?: { yardId?: string } }; fulfilledTimeStamp?: number };
          }
        ) => {
          // Site-switch containment — the FOURTH seed. Scoped by `yardId` like
          // goals/events; it only looks different because the endpoint lives on
          // `yardApis`. Without this the gate reports success while visits from
          // the previous yard still land, and because visits are grouped by bare
          // `locationCode` (reused across yards) they paint phantom occupancy
          // bars, TDR-in badges and equipment numbers on the new site's rows.
          if (isStaleSiteSeed(state.currentYardId, action.meta?.arg?.originalArgs?.yardId)) {
            rumService.warn("Dropped visit seed for a previous site", {
              requested: action.meta?.arg?.originalArgs?.yardId,
              current: state.currentYardId,
            });
            return;
          }
          // First-paint seed latch — see `seededSources`.
          state.seededSources.visits = true;
          const gate = resolveSeedGate(state, "visits", action.meta?.fulfilledTimeStamp);
          const visits = (action.payload || []) as VersionedVisit[];
          for (const row of visits) {
            const id = resolveVisitEntityId(row);
            if (!id) {
              rumService.warn("Visit missing ID — skipping normalization during API seed", {
                row,
              });
              continue;
            }
            // API-seed default: absent version → "0".
            const visit = { ...row, datapathViewVersion: row.datapathViewVersion || "0" };
            // The gate widens the LIVE-entity check only; the retained snapshot gate
            // stays strict on every seed path, catch-up included.
            ingestVisit(state, visit, gate);
          }
        }
      )
      // Seed appointments from getYardAppointments API response
      .addMatcher(ryteApis.endpoints.getYardAppointments.matchFulfilled, (state, action) => {
        const { payload } = action;
        // buildingCode, NOT yardId — this request is scoped differently.
        if (
          isStaleSiteSeed(state.currentBuildingCode, action.meta?.arg?.originalArgs?.buildingCode)
        ) {
          rumService.warn("Dropped appointment seed for a previous site", {
            requested: action.meta?.arg?.originalArgs?.buildingCode,
            current: state.currentBuildingCode,
          });
          return;
        }
        // First-paint seed latch — see `seededSources`.
        state.seededSources.appointments = true;
        const gate = resolveSeedGate(state, "appointments", action.meta?.fulfilledTimeStamp);
        // Normalize: strip isMockData so real API data is authoritative —
        // RTK's shallow upsert overwrites any prior marker-backed `true`.
        const rows = ((payload.appointments || []) as VersionedAppointment[]).map((row) => ({
          ...row,
          isMockData: undefined,
        }));
        seedEntities(
          state.appointments,
          appointmentsAdapter,
          rows,
          (row) => row.appointmentId,
          "Appointment",
          gate
        );
      })
      // Seed events from getYardEvents API response in one sorted-adapter batch.
      .addMatcher(ryteApis.endpoints.getYardEvents.matchFulfilled, (state, action) => {
        const { payload } = action;
        if (isStaleSiteSeed(state.currentYardId, action.meta?.arg?.originalArgs?.yardId)) {
          rumService.warn("Dropped event seed for a previous site", {
            requested: action.meta?.arg?.originalArgs?.yardId,
            current: state.currentYardId,
          });
          return;
        }
        // First-paint seed latch — see `seededSources`.
        state.seededSources.events = true;
        seedEventEntities(
          state.events,
          (payload.events || []) as VersionedYardEventEnvelope[],
          resolveSeedGate(state, "events", action.meta?.fulfilledTimeStamp)
        );
      })
      // Seed moves from getYardMoves API response. The endpoint is shared with
      // RYC's own `yardMoves` slice, which keeps a separate copy — RYTE stores
      // moves in the normalized `ryteData` slice so `clearRyteData` empties them
      // on site change / refresh alongside the other entities. Scoped by
      // `yardId` like goals/events via `isStaleSiteSeed`.
      //
      // Deliberately NOT tracked in `seededSources`: moves are not a first-paint
      // readiness dependency (they never move, add, or re-anchor a chart bar), so
      // adding a fifth latch would make the gate wait on data the chart's first
      // stable paint does not need. Uses `shouldAcceptApiSeed` (via
      // `seedEntities`) so a refetch refreshes existing "0"-seeded moves.
      .addMatcher(
        yardApis.endpoints.getYardMoves.matchFulfilled,
        (
          state,
          action: {
            payload: GetYardMovesOutput | undefined;
            meta?: { arg?: { originalArgs?: { yardId?: string } }; fulfilledTimeStamp?: number };
          }
        ) => {
          if (isStaleSiteSeed(state.currentYardId, action.meta?.arg?.originalArgs?.yardId)) {
            rumService.warn("Dropped move seed for a previous site", {
              requested: action.meta?.arg?.originalArgs?.yardId,
              current: state.currentYardId,
            });
            return;
          }
          seedEntities(
            state.moves,
            movesAdapter,
            action.payload?.moves ?? [],
            (row) => row.moveId,
            "Move",
            resolveSeedGate(state, "moves", action.meta?.fulfilledTimeStamp)
          );
        }
      );
  },
});

// ─── Exports ─────────────────────────────────────────────────────────────────

export const {
  upsertGoal,
  upsertManyGoals,
  removeGoal,
  removeManyGoals,
  replaceGoal,
  upsertVisit,
  upsertManyVisits,
  removeVisit,
  removeManyVisits,
  upsertAppointment,
  upsertManyAppointments,
  removeAppointment,
  removeManyAppointments,
  upsertEvent,
  upsertManyEvents,
  removeEvent,
  removeManyEvents,
  upsertMove,
  upsertManyMoves,
  removeMove,
  removeManyMoves,
  clearRyteMockData,
  clearRyteData,
  armRyteCatchUp,
  setRyteSiteScope,
} = ryteDataSlice.actions;

export default ryteDataSlice.reducer;
